import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Send, MessageCircle, Loader2, CheckCircle, AlertCircle, EyeOff } from 'lucide-react';
import logo from '../assets/images/pulso-logo.png';
import { LEGAL } from './legal/legalDetails';
import '../styles/pulso.css';
import '../styles/legal.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

export default function HomePage() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [contactType, setContactType] = useState('email');
  const [message, setMessage] = useState('');
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submittedAnonymously, setSubmittedAnonymously] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');
    try {
      const response = await fetch(`${API_URL}/api/web/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: isAnonymous ? '' : name,
          email: isAnonymous ? '' : email,
          phone: isAnonymous ? '' : phone,
          contactType: isAnonymous ? 'none' : contactType,
          message,
          isAnonymous,
        }),
      });
      const data = await response.json();
      if (response.ok && data.success) {
        setSubmittedAnonymously(isAnonymous);
        setIsSubmitted(true);
        setName(''); setEmail(''); setPhone(''); setMessage(''); setIsAnonymous(false);
      } else {
        setError(data.error || 'Failed to send message. Please try again.');
      }
    } catch {
      setError('Failed to connect to server. Please check your connection and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="hp-root">

      {/* HEADER */}
      <header className="hp-header">
        <div className="hp-header-inner">
          <Link to="/" className="nav-logo">
            <img src={logo} alt="Pulso" className="nav-logo-img" style={{ width: 34 }} />
            <div className="nav-logo-text-wrap">
              <span className="nav-logo-name">PULSO</span>
              <span className="nav-logo-by">by Imaginarium</span>
            </div>
          </Link>
          <Link to="/login" className="hp-admin-btn">Admin Login</Link>
        </div>
      </header>

      {/* MAIN */}
      <main className="hp-main">

        <div className="hp-hero">
          <div className="hp-hero-icon">
            <MessageCircle size={28} color="#f0856d" />
          </div>
          <h1 className="hp-h1">Get in Touch</h1>
          <p className="hp-sub">
            Have a question or concern? Send us a message and our team will get back to you as soon as possible.
          </p>
          <a href="https://wa.me/27600185754" target="_blank" rel="noopener noreferrer"
            className="hp-whatsapp-btn">
            <MessageCircle size={18} />
            Message us on WhatsApp
          </a>
        </div>

        <div className="hp-card">
          <div className="hp-card-header">
            <div className="hp-card-title">
              <Send size={18} color="#f0856d" />
              Send a Message
            </div>
            <p className="hp-card-desc">Fill out the form below and we'll respond promptly.</p>
          </div>

          <div className="hp-card-body">
            {isSubmitted ? (
              <div className="hp-success">
                <div className="hp-success-icon">
                  <CheckCircle size={28} color="#f0856d" />
                </div>
                <h3>Message Sent</h3>
                <p>Thank you for reaching out. We'll get back to you soon.</p>
                {submittedAnonymously && (
                  <div className="hp-anon-notice" style={{ marginTop: 16 }}>
                    <EyeOff size={13} style={{ flexShrink: 0, opacity: 0.6 }} />
                    If you report anonymously, we will attend to your query with the same level of attention as any other, but of course are unable to respond directly to you.
                  </div>
                )}
                <button
                  type="button"
                  className="hp-submit"
                  style={{ marginTop: 20 }}
                  onClick={() => {
                    setIsSubmitted(false);
                    setSubmittedAnonymously(false);
                  }}
                >
                  Send another message
                </button>
              </div>
            ) : (
              <>
                {error && (
                  <div className="p-error">
                    <AlertCircle size={16} style={{ flexShrink: 0 }} />
                    <span>{error}</span>
                  </div>
                )}

                <form onSubmit={handleSubmit}>

                  {/* ── Anonymous toggle ── */}
                  <div className="hp-anon-wrap">
                    <label className={`hp-anon-toggle${isAnonymous ? ' is-active' : ''}`}>
                      <input
                        type="checkbox"
                        checked={isAnonymous}
                        onChange={e => setIsAnonymous(e.target.checked)}
                        disabled={isSubmitting}
                        className="hp-anon-checkbox"
                      />
                      <span className="hp-anon-box">
                        {isAnonymous && (
                          <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                            <path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        )}
                      </span>
                      <EyeOff size={14} className="hp-anon-icon" />
                      <span className="hp-anon-text">Post anonymously</span>
                    </label>

                    {isAnonymous && (
                      <div className="hp-anon-notice">
                        <EyeOff size={13} style={{ flexShrink: 0, opacity: 0.6 }} />
                        If you report anonymously, we will attend to your query with the same level of attention as any other, but of course are unable to respond directly to you.
                      </div>
                    )}
                  </div>

                  {/* Name + contact fields — hidden when anonymous */}
                  {!isAnonymous && (
                    <>
                      <div className="p-field">
                        <label className="p-label-text">Name</label>
                        <input className="p-input" type="text" placeholder="Your name"
                          value={name} onChange={e => setName(e.target.value)}
                          required disabled={isSubmitting} />
                      </div>

                      <div className="p-field">
                        <label className="p-label-text">How should we contact you back?</label>
                        <div className="hp-toggle">
                          {[{ value: 'email', label: 'Email' }, { value: 'phone', label: 'Phone / WhatsApp' }].map(opt => (
                            <label key={opt.value}
                              className={`hp-toggle-opt${contactType === opt.value ? ' active' : ''}`}>
                              <input type="radio" name="contactType" value={opt.value}
                                checked={contactType === opt.value}
                                onChange={() => setContactType(opt.value)} />
                              {opt.label}
                            </label>
                          ))}
                        </div>
                      </div>

                      {contactType === 'email' && (
                        <div className="p-field">
                          <label className="p-label-text">Email address</label>
                          <input className="p-input" type="email" placeholder="your@email.com"
                            value={email} onChange={e => setEmail(e.target.value)}
                            required disabled={isSubmitting} />
                        </div>
                      )}

                      {contactType === 'phone' && (
                        <div className="p-field">
                          <label className="p-label-text">Phone number</label>
                          <input className="p-input" type="tel" placeholder="+27 82 000 0000"
                            value={phone} onChange={e => setPhone(e.target.value)}
                            required disabled={isSubmitting} />
                          <p className="p-hint">Include your country code so we can reach you via SMS or WhatsApp.</p>
                        </div>
                      )}
                    </>
                  )}

                  <div className="p-field">
                    <label className="p-label-text">Message</label>
                    <textarea className="p-textarea" placeholder="How can we help you?"
                      value={message} onChange={e => setMessage(e.target.value)}
                      required disabled={isSubmitting} rows={5} />
                  </div>

                  <button type="submit" className="hp-submit" disabled={isSubmitting}>
                    {isSubmitting
                      ? <><Loader2 size={16} className="animate-spin" /> Sending...</>
                      : <><Send size={16} /> Send Message</>
                    }
                  </button>

                </form>
              </>
            )}
          </div>
        </div>

      </main>

      <footer className="hp-footer">
        <nav className="lg-links" aria-label="Legal">
          <Link to="/about">About</Link>
          <Link to="/privacy">Privacy Policy</Link>
          <Link to="/terms">Terms of Service</Link>
          <Link to="/data-deletion">Data Deletion</Link>
        </nav>
        © {new Date().getFullYear()} PULSO · From the kitchen of IMGNRM.
        Pulso is operated by {LEGAL.companyName}, the parent company of Imaginarium.
      </footer>

    </div>
  );
}